import type { TablesInfo } from 'src/queries/tableServers';

export interface FilterTableKeysOptions {
    keys: readonly string[];
    /** Space-separated search terms; every term must match for a key to be kept. */
    search?: string;
    tablesInfo: TablesInfo | undefined;
    /** Keep only tables held by this server; blank keeps all. */
    tableServerFilter?: string;
}

/** `parseKey` throws on keys without a colon; such keys are treated as all-symbol, no exchange. */
const splitKey = (key: string): { sym: string; ex: string } => {
    const lastColon = key.lastIndexOf(':');
    return lastColon === -1 ? { sym: key, ex: '' } : { sym: key.slice(0, lastColon), ex: key.slice(lastColon + 1) };
};

/**
 * Matches one search term against a table key, case-insensitively:
 * - `:X` (a lone leading colon) matches the key's exchange suffix, as before
 * - a term containing any other colon is matched against the whole key, so a full key, an
 *   intermediate `sym:ex` segment, or a pasted tail all match (`#` and `:` are ordinary characters)
 * - anything else matches within the symbol part only
 */
export const tableKeyMatchesTerm = (key: string, term: string): boolean => {
    const needle = term.toLowerCase();
    const { sym, ex } = splitKey(key);
    if (needle.startsWith(':') && needle.indexOf(':', 1) === -1) {
        return ex.toLowerCase().includes(needle.slice(1));
    }
    if (needle.includes(':')) {
        return key.toLowerCase().includes(needle);
    }
    return sym.toLowerCase().includes(needle);
};

/** Filters the browser's key list by server and by every whitespace-separated search term. */
export const filterTableKeys = ({
    keys,
    search = '',
    tablesInfo,
    tableServerFilter = '',
}: FilterTableKeysOptions): string[] => {
    let filtered = [...keys];
    if (tableServerFilter !== '') {
        filtered = filtered.filter(
            (key) => tablesInfo?.[key]?.servers.some((server) => server.id === tableServerFilter) ?? false,
        );
    }
    for (const term of search.split(/\s+/)) {
        if (term === '') continue;
        filtered = filtered.filter((key) => tableKeyMatchesTerm(key, term));
    }
    return filtered;
};
